import { COUNTRY_CODES } from "../../../../constants/countries";
import { GRAVEDAD } from "../costarica/validacionDeHacienda";
import {
  TIPOS_DE_CLIENTE_FE,
  llevaDigitoVerificador,
  normalizarTipoDeCliente,
} from "./datosFiscalesDePanama";
import { FORMA_DEL_CODIGO, ubicacionDelCodigo } from "./ubicacionesDGI";

/**
 * Lo que la DGI o Business Central van a rechazar en Panamá, dicho antes de enviarlo.
 *
 * Mismo principio que `costarica/validacionDeHacienda.js`: se ejecuta en el tronco
 * de la sincronización, antes de que nada salga del terminal, así que el documento
 * sigue vivo y se corrige el dato.
 *
 * ## Dos rechazos distintos
 *
 * En Panamá un documento puede caerse en dos sitios, y los dos se miran aquí:
 *
 * 1. **En Business Central, al registrar.** La migración de Panamá le hace
 *    `TestField` al RUC y al correo del cliente y al código de ubicación, y da
 *    error si el cliente no tiene tipo de cliente FE o si una línea de una factura
 *    va a precio cero (`Codeunit 57000:751-798`, `Codeunit 50504:18-36`).
 * 2. **En la DGI, al autorizar.** Las reglas de la ficha técnica de la FE para
 *    PAC, v1.00 de abril de 2025, sección 6.4.2 y tabla de validaciones: el RUC
 *    jurídico en un consumidor final (1621), el nombre de un contribuyente (1605),
 *    el descuento de línea igual o mayor que el precio (2051).
 *
 * ## Tres estados, no dos
 *
 * El tipo de cliente, el DV y el código de ubicación son campos que Business
 * Central **todavía no publica** en `ListaClientes`. Mientras no lo haga, el
 * terminal no los conoce: `undefined` quiere decir «no se sabe», no «falta». Lo que
 * no se sabe no bloquea una venta --se deja en `REVISAR`--; lo que se sabe que falta,
 * sí. En cuanto BC los publique, las mismas reglas empiezan a bloquear solas.
 *
 * ## Qué bloquea y qué sólo avisa
 *
 * Donde la ficha técnica marca una validación como notificación (N) y no como
 * rechazo (R), aquí es `REVISAR`. Y donde lo medido contradice a la ficha, manda lo
 * medido: el teléfono. La DGI lo pide como `999-9999` o parecido, pero en PAN DEV
 * facturas con `(99) 9999-9999` o `9999999` obtuvieron su CUFE; las que no lo
 * obtuvieron son las de dos números en el mismo campo, con barra o con espacio
 * (0 de 6). Eso bloquea; el resto avisa. `12 - Panamá/test-2026-09-17/cruce-cufe-PAN-DEV.log`.
 */

const texto = (valor) => String(valor ?? "").trim();
const problema = (campo, aviso, gravedad = GRAVEDAD.RECHAZA) => ({ campo, aviso, gravedad });
const noSeSabe = (valor) => valor === undefined || valor === null;

/**
 * Si el terminal está emitiendo ya la factura electrónica de Panamá.
 *
 * Hoy **no**: CertificaE todavía no la desarrolla, y mientras tanto el terminal
 * adelanta el camino de venta → factura borrador → registro automático, como en
 * Costa Rica (decisión D4 de `12 - Panamá/campos-excpan-tpv-2026-09-17.md`).
 *
 * Mientras no se emita, **lo que sólo pide la DGI no puede frenar una venta que
 * Business Central registraría sin problema**: se avisa, pero no se bloquea. Lo
 * que Business Central sí exige --la identificación del cliente, que ninguna línea
 * vaya a cero-- sigue bloqueando igual.
 *
 * Medido en PAN DEV el 17/09/2026: 442 de 1.839 fichas (24 %) no tienen correo.
 * Con el correo bloqueando, una de cada cuatro ventas no saldría, por un dato que
 * hoy no se usa para nada.
 *
 * El día que CertificaE entregue, esto pasa a `true` y todas las reglas vuelven a
 * frenar. No hay nada más que tocar.
 */
export const LA_FE_DE_PANAMA_SE_EMITE = false;

const MIENTRAS_NO_SE_EMITE =
  "No frena la venta todavía, porque la factura electrónica no se está emitiendo, " +
  "pero hay que corregirlo en la ficha antes de que se emita.";

/**
 * Un aviso que sólo pide la DGI. Mientras la factura electrónica no se emita, deja
 * de rechazar y pasa a avisar, con el motivo dicho.
 */
const avisoDeLaDGI = (aviso) => {
  if (!aviso || LA_FE_DE_PANAMA_SE_EMITE || aviso.gravedad !== GRAVEDAD.RECHAZA) return aviso;
  return { ...aviso, gravedad: GRAVEDAD.REVISAR, aviso: `${aviso.aviso} ${MIENTRAS_NO_SE_EMITE}` };
};

/** Los formatos de teléfono de la ficha técnica (`dTfnRec`, B408). */
const TELEFONO_DGI = /^(\d{3,4}-\d{4}|\d{6}-\d{3,4}-\d{4})$/;

/** Un correo con forma de correo. La DGI pide «correo válido» (B409) sin más detalle. */
const CORREO = /^[^\s@;,/]+@[^\s@;,/]+\.[^\s@;,/]{2,}$/;

const LARGO_DEL_RUC = { minimo: 5, maximo: 20 };
const LARGO_DEL_NOMBRE = 100;
const LARGO_DE_LA_DIRECCION = 100;

/**
 * El tipo de cliente de la factura electrónica.
 *
 * Sin él, Business Central no registra: la migración da error al armar el XML.
 */
export const revisarTipoDeCliente = (valor) => {
  if (noSeSabe(valor)) {
    return problema(
      "Tipo de cliente FE",
      "Business Central todavía no publica el tipo de cliente de la factura electrónica: no se pudo comprobar.",
      GRAVEDAD.REVISAR
    );
  }
  const tipo = normalizarTipoDeCliente(valor);
  if (!tipo) {
    return problema(
      "Tipo de cliente FE",
      "El cliente no tiene tipo de cliente para la factura electrónica. Elige Contribuyente, Consumidor final, Gobierno o Extranjero en su ficha."
    );
  }
  if (!Object.values(TIPOS_DE_CLIENTE_FE).includes(tipo)) {
    return problema(
      "Tipo de cliente FE",
      `«${tipo}» no es un tipo de cliente de la DGI. Elige Contribuyente, Consumidor final, Gobierno o Extranjero.`
    );
  }
  return null;
};

/**
 * La identificación: RUC, cédula o, para un extranjero, pasaporte.
 *
 * Business Central exige el RUC en la cabecera para registrar, sea del tipo que
 * sea el cliente (`Codeunit 50504:36`), y el XML lo manda siempre salvo en
 * exportación. La DGI admite de 5 a 20 caracteres (`dRuc`, B4022).
 */
export const revisarIdentificacion = ({ numero, tipoDeCliente, tipoDeIdentificacion }) => {
  const avisos = [];
  const crudo = String(numero ?? "");
  const limpio = crudo.trim();
  const esExtranjero = tipoDeCliente === TIPOS_DE_CLIENTE_FE.EXTRANJERO;

  if (!limpio) {
    avisos.push(
      problema(
        "Identificación",
        "Business Central no registra la factura sin la identificación del cliente (RUC, cédula o pasaporte)."
      )
    );
    return avisos;
  }

  if (crudo !== limpio) {
    avisos.push(
      problema(
        "Identificación",
        `«${crudo}» lleva espacios al principio o al final. Quítalos en la ficha: con ellos la factura se quedó sin autorizar en uno de cada cinco casos medidos.`,
        GRAVEDAD.REVISAR
      )
    );
  }

  if (!esExtranjero && (limpio.length < LARGO_DEL_RUC.minimo || limpio.length > LARGO_DEL_RUC.maximo)) {
    avisos.push(
      problema(
        "Identificación",
        `«${limpio}» no puede ser un RUC ni una cédula: la DGI admite de ${LARGO_DEL_RUC.minimo} a ${LARGO_DEL_RUC.maximo} caracteres.`
      )
    );
  }

  // El tipo de contribuyente (natural o jurídico) viaja con el RUC. Un extranjero
  // no lo lleva: va con su identificación extranjera.
  if (!esExtranjero && !texto(tipoDeIdentificacion)) {
    avisos.push(
      problema(
        "Tipo de identificación",
        "Falta si el cliente es persona natural o jurídica: la DGI lo pide junto al RUC."
      )
    );
  }

  // 1621: un consumidor final no puede llevar RUC de persona jurídica.
  if (
    tipoDeCliente === TIPOS_DE_CLIENTE_FE.CONSUMIDOR_FINAL &&
    texto(tipoDeIdentificacion) === "Legal Entity"
  ) {
    avisos.push(
      problema(
        "Tipo de cliente FE",
        "Un consumidor final no puede llevar RUC de persona jurídica: la DGI lo rechaza (1621). Cambia el tipo de cliente a Contribuyente."
      )
    );
  }

  return avisos;
};

/**
 * El dígito verificador del RUC: sólo contribuyente y gobierno.
 *
 * Que corresponda de verdad al RUC (1601) no se puede saber sin preguntarle a la
 * DGI: la ficha técnica no publica el algoritmo. Aquí sólo se mira que esté y que
 * sean dígitos.
 */
export const revisarDigitoVerificador = (valor, tipoDeCliente) => {
  if (!llevaDigitoVerificador(tipoDeCliente)) return null;
  if (noSeSabe(valor)) {
    return problema(
      "DV del RUC",
      "Business Central todavía no publica el dígito verificador: no se pudo comprobar.",
      GRAVEDAD.REVISAR
    );
  }
  const dv = texto(valor);
  if (!dv) {
    const quien =
      tipoDeCliente === TIPOS_DE_CLIENTE_FE.GOBIERNO ? "Un cliente de gobierno" : "Un contribuyente";
    return problema(
      "DV del RUC",
      `${quien} necesita el dígito verificador del RUC. Captúralo en su ficha.`
    );
  }
  if (!/^\d{1,2}$/.test(dv)) {
    return problema("DV del RUC", `«${dv}» no es un dígito verificador: son dos cifras.`);
  }
  return null;
};

/**
 * El código de ubicación: provincia, distrito y corregimiento.
 *
 * Que exista lo exige Business Central (`TestField`, 57000:783). Que esté en el
 * catálogo de la DGI es una notificación (1616, N), así que un código desconocido
 * sólo avisa.
 */
export const revisarCodigoDeUbicacion = (valor) => {
  if (noSeSabe(valor)) {
    return problema(
      "Ubicación",
      "Business Central todavía no publica el código de ubicación del cliente: no se pudo comprobar.",
      GRAVEDAD.REVISAR
    );
  }
  const codigo = texto(valor);
  if (!codigo) {
    return problema(
      "Ubicación",
      "El cliente no tiene provincia, distrito y corregimiento de la DGI. Business Central no registra la factura sin ellos: elígelos en su ficha."
    );
  }
  if (!FORMA_DEL_CODIGO.test(codigo) || !ubicacionDelCodigo(codigo)) {
    return problema(
      "Ubicación",
      `«${codigo}» no está en el catálogo de ubicaciones de la DGI. Vuelve a elegir el corregimiento en la ficha.`,
      GRAVEDAD.REVISAR
    );
  }
  return null;
};

/** El correo: obligatorio (`TestField`, 57000:798) y uno solo. */
export const revisarCorreoDGI = (valor) => {
  const correo = texto(valor);
  if (!correo) {
    return problema(
      "Correo electrónico",
      "Business Central no registra la factura sin el correo del cliente. Ninguna factura medida sin correo obtuvo autorización."
    );
  }
  if (/[;,/\s]/.test(correo)) {
    return problema(
      "Correo electrónico",
      `«${correo}» parece llevar más de una dirección. En el comprobante viaja una sola.`
    );
  }
  if (!CORREO.test(correo)) {
    return problema("Correo electrónico", `«${correo}» no tiene formato de correo.`);
  }
  return null;
};

/**
 * El teléfono. Es opcional, pero el que se manda tiene que poder leerse como uno.
 *
 * Bloquea sólo lo que se midió que no pasa: dos números en el mismo campo. El
 * resto de formatos que no son los de la ficha técnica avisan.
 */
export const revisarTelefonoDGI = (valor) => {
  const telefono = texto(valor);
  if (!telefono) return null;

  if (/\//.test(telefono) || /\d[\s]+\d/.test(telefono.replace(/^\(\d+\)\s*/, ""))) {
    return problema(
      "Teléfono",
      `«${telefono}» parece llevar más de un número. Deja uno solo en la ficha del cliente, por ejemplo 999-9999.`
    );
  }
  if (!TELEFONO_DGI.test(telefono)) {
    return problema(
      "Teléfono",
      `«${telefono}» no tiene el formato de la DGI (999-9999, 9999-9999, 999999-999-9999 o 999999-9999-9999).`,
      GRAVEDAD.REVISAR
    );
  }
  return null;
};

/**
 * Nombre y dirección. Obligatorios para contribuyente y gobierno; el nombre es
 * rechazo (1605) y la dirección, notificación (1606). Los dos, hasta 100.
 */
export const revisarNombreYDireccion = ({ nombre, direccion, tipoDeCliente }) => {
  const avisos = [];
  const esDeLosQueSeIdentifican = llevaDigitoVerificador(tipoDeCliente);

  if (!texto(nombre)) {
    if (esDeLosQueSeIdentifican) {
      avisos.push(problema("Nombre", "Un contribuyente o un gobierno tienen que llevar razón social: la DGI lo rechaza sin ella (1605)."));
    }
  } else if (texto(nombre).length > LARGO_DEL_NOMBRE) {
    avisos.push(problema("Nombre", `El nombre pasa de ${LARGO_DEL_NOMBRE} caracteres, que es lo que admite la DGI.`));
  }

  if (!texto(direccion)) {
    if (esDeLosQueSeIdentifican) {
      avisos.push(problema("Dirección", "Un contribuyente o un gobierno deberían llevar dirección (1606).", GRAVEDAD.REVISAR));
    }
  } else if (texto(direccion).length > LARGO_DE_LA_DIRECCION) {
    avisos.push(problema("Dirección", `La dirección pasa de ${LARGO_DE_LA_DIRECCION} caracteres, que es lo que admite la DGI.`));
  }

  return avisos;
};

/**
 * Las líneas.
 *
 * - **Precio cero en una factura:** la migración no registra con el tipo de venta
 *   «Factura», que es el que toma una venta de caja (`Codeunit 50504:26-29`).
 * - **Importe cero en cualquier documento:** no se libera (`Codeunit 50519:69-98`).
 * - **Descuento del 100 %:** la DGI rechaza un descuento de línea igual o mayor que
 *   el precio (2051). Para regalar hay otra naturaleza de operación.
 */
export const revisarLineasDGI = (lineas, { esNotaDeCredito = false } = {}) => {
  const avisos = [];

  (lineas || []).forEach((linea, i) => {
    const donde = `Línea ${i + 1}${linea?.No ? ` (${linea.No})` : ""}`;
    // En valor absoluto, porque **en una nota de crédito las cantidades van en
    // negativo**: es el convenio del terminal, que las fuerza con `-Math.abs()`
    // (`InvoiceLines.js:1045`). Mirar el signo hacía que ninguna nota de crédito
    // de Panamá saliera del terminal, con un aviso que además pedía lo contrario
    // de lo que el propio terminal acababa de escribir.
    //
    // Lo que rechaza Business Central es el **cero**, no el signo: la línea a
    // importe cero al liberar (`MIGPAN 50519:69-98`, y sólo cuando la cantidad no
    // es cero) y el precio cero al registrar (`50504:26-30`).
    const cantidad = Math.abs(Number(linea?.Quantity ?? 0));
    const precio = Math.abs(Number(linea?.Unit_Price ?? 0));
    const descuento = Number(linea?.Line_Discount_ ?? 0);

    if (!(cantidad > 0)) {
      avisos.push(problema(donde, "La cantidad no puede ser cero."));
    }
    if (!(precio > 0)) {
      avisos.push(
        problema(
          donde,
          esNotaDeCredito
            ? "Business Central no libera un documento con una línea de importe cero."
            : "Business Central no registra una factura con una línea a precio cero."
        )
      );
    } else if (descuento >= 100) {
      avisos.push(
        problema(donde, "La DGI rechaza un descuento de línea del 100 % (2051). Deja un precio mayor que cero.")
      );
    }
  });

  return avisos;
};

/**
 * Todo junto.
 *
 * El país se comprueba **aquí dentro**, igual que en Costa Rica: estas reglas
 * corren desde el tronco por donde pasan todos los países.
 *
 * @param {object} args
 * @param {object} args.documento  La cabecera local.
 * @param {object} [args.ficha]    El cliente local: de ahí saca Business Central casi todo el XML.
 * @param {Array}  args.lineas
 * @param {boolean} [args.esNotaDeCredito]
 * @param {string} args.countryCode  Sin país no se valida.
 * @returns {Array<{campo: string, aviso: string, gravedad: string}>}
 */
export const revisarParaLaDGI = ({ documento, ficha, lineas, esNotaDeCredito, countryCode } = {}) => {
  if (countryCode !== COUNTRY_CODES.PANAMA) return [];

  const avisos = [];

  if (!ficha) {
    avisos.push(
      problema(
        "Cliente",
        "No se encontró la ficha local del cliente, y de ahí saca Business Central los datos de la factura electrónica.",
        GRAVEDAD.REVISAR
      )
    );
  } else {
    const tipoDeCliente = normalizarTipoDeCliente(ficha.Tipo_Cliente_FE);
    avisos.push(avisoDeLaDGI(revisarTipoDeCliente(ficha.Tipo_Cliente_FE)));

    // Que falte la identificación lo rechaza Business Central al validar el
    // cliente en el borrador, así que ésa frena siempre. El resto de lo que mira
    // esta revisión --el largo del RUC, si es persona natural o jurídica, el 1621--
    // sólo lo mira la DGI.
    const identificacion = revisarIdentificacion({
      numero: ficha.Numero_Identificacion_fiscal,
      tipoDeCliente,
      tipoDeIdentificacion: ficha.Tipo_Documento_SrI,
    });
    const faltaLaIdentificacion = !texto(ficha.Numero_Identificacion_fiscal);
    avisos.push(...(faltaLaIdentificacion ? identificacion : identificacion.map(avisoDeLaDGI)));

    avisos.push(avisoDeLaDGI(revisarDigitoVerificador(ficha.Ruc_DV, tipoDeCliente)));
    avisos.push(avisoDeLaDGI(revisarCodigoDeUbicacion(ficha.Codigo_Ubicacion_FE)));
    avisos.push(avisoDeLaDGI(revisarCorreoDGI(ficha.E_Mail)));
    if (tipoDeCliente !== TIPOS_DE_CLIENTE_FE.EXTRANJERO)
      avisos.push(avisoDeLaDGI(revisarTelefonoDGI(ficha.Telefono)));
    avisos.push(
      ...revisarNombreYDireccion({
        nombre: ficha.name,
        direccion: ficha.Direccion,
        tipoDeCliente,
      }).map(avisoDeLaDGI)
    );
  }

  // Sin ficha, al menos el RUC de la cabecera, que Business Central exige al
  // registrar. Con ficha ya lo dijo `revisarIdentificacion`: BC copia el de la
  // ficha cuando la cabecera no lo trae.
  if (!ficha && documento && !texto(documento.VAT_Registration_No_)) {
    avisos.push(
      problema("Identificación", "El documento no lleva la identificación del cliente, y Business Central no lo registra sin ella.")
    );
  }

  avisos.push(...revisarLineasDGI(lineas, { esNotaDeCredito: Boolean(esNotaDeCredito) }));

  // Un mismo aviso puede salir por la ficha y por la cabecera: se dice una vez.
  const vistos = new Set();
  return avisos.filter(Boolean).filter((a) => {
    const clave = `${a.campo}|${a.aviso}`;
    if (vistos.has(clave)) return false;
    vistos.add(clave);
    return true;
  });
};
